import { ChangeContext, Options } from '@angular-slider/ngx-slider';
import { formatDate } from '@angular/common';
import { ChangeDetectorRef, inject, Injectable, OnDestroy } from '@angular/core';
import { dsDtos as dsDtosAdmin } from '@vectronic/openApi-admin';
import { dsDtos as dsDtosCustomer, webDtos } from '@vectronic/openApi-customer';
import { baseHref } from '@vectronic/ui';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { L } from '../../services/leaflet-setup/leaflet-setup';
import { MapMarkerService } from '../../services/map/map-marker.service';
import { MapService } from '../../services/map/map.service';
import { Position, TrackplayCalculationService } from './trackplay-calculation.service';

declare let requestAnimationFrame: (callback: FrameRequestCallback) => number;
declare let cancelAnimationFrame: (handle: number) => void;

export interface PlaybackConfig {
  fadeOutOption: number;
  speedIndex: number;
  autoFocus: boolean;
}

export interface MotionTrack {
  motionTrack: any;
  positions: any;
  startTime: any;
  endTime: any;
  pauseTimestamp?: any;
  hasStarted: boolean;
  id: any;
  color: string;
}

interface TimeRange {
  minTime: number;
  maxTime: number;
}

@Injectable()
export class TrackplayService implements OnDestroy {
  private mapService = inject(MapService);
  private mapMarkerService = inject(MapMarkerService);
  private calcService = inject(TrackplayCalculationService);
  private cdr = inject(ChangeDetectorRef);

  mapPositions: webDtos.MapPositionsList[] | undefined;
  speciesMap:
    | Map<number, dsDtosCustomer.AnimalSpecies | dsDtosAdmin.AnimalSpeciesInput>
    | undefined;

  loading = false;
  isProcessing = false;
  motionTracks: MotionTrack[] = [];
  private activeSegments: Map<string, L.LatLng[]> = new Map();
  private dateCache: Map<number, string> | undefined;
  private activeSegmentsQueue: {
    trackId: string;
    segment: any;
    endTimestamp: number;
  }[] = [];

  readonly TIME_RANGE_OPTIONS = [
    { value: 0, viewValue: 'Never' },
    { value: 24 * 60 * 60 * 1000, viewValue: '1 day' },
    { value: 72 * 60 * 60 * 1000, viewValue: '3 days' },
    { value: 7 * 24 * 60 * 60 * 1000, viewValue: '1 week' },
    { value: 14 * 24 * 60 * 60 * 1000, viewValue: '2 weeks' },
    { value: 30 * 24 * 60 * 60 * 1000, viewValue: '1 month' },
    { value: 90 * 24 * 60 * 60 * 1000, viewValue: '3 months' },
    { value: 180 * 24 * 60 * 60 * 1000, viewValue: '6 months' },
    { value: 365 * 24 * 60 * 60 * 1000, viewValue: '1 year' },
    { value: 1, viewValue: 'No Track' },
  ];

  sliderStepSize = 0;
  options: Options | undefined;
  minValue = 0;
  maxValue = 0;
  cachedMaxValue: number | null = null; // for replay track
  isSliderChanged = false;
  trackPlayedFirstTime = true; // used to show highlight track for no Track fadeout option
  private lastPanLocation: L.LatLng | null = null;
  private highlightLayerGroup: L.LayerGroup | null = null;
  private highlightMarkersGroup: L.LayerGroup | null = null;
  private motionLayerGroup: L.LayerGroup | undefined;
  private fullTrackPositions: {
    id: number;
    positions: { latlng: L.LatLng; timestamp: number }[];
    color: string;
  }[] = [];
  ceil = 0;
  floor = 0;
  selectedTimeRange = 0;

  defaultConfig: PlaybackConfig = {
    speedIndex: 10,
    fadeOutOption: 90 * 24 * 60 * 60 * 1000,
    autoFocus: true,
  };

  private sliderChangeSubject = new Subject<{ min: number; max: number }>();
  private sliderChangeSubscription: Subscription;

  readonly TIME_SETTINGS: { label: string; value: number }[] = [
    { label: '1min/sec', value: 60 },
    { label: '2min/sec', value: 120 },
    { label: '5min/sec', value: 300 },
    { label: '10min/sec', value: 600 },
    { label: '15min/sec', value: 900 },
    { label: '30min/sec', value: 1800 },
    { label: '1h/sec', value: 3600 },
    { label: '2h/sec', value: 7200 },
    { label: '4h/sec', value: 14400 },
    { label: '8h/sec', value: 28800 },
    { label: '12h/sec', value: 43200 },
    { label: '24h/sec', value: 86400 },
    { label: '2 days/sec', value: 172800 },
    { label: '4 days/sec', value: 172800 },
    { label: '1 week/sec', value: 604800 },
    { label: '2 week/sec', value: 604800 * 2 },
  ];

  private readonly DEFAULT_LINE_WEIGHT = 3;
  private readonly MOTION_OPACITY = 1;
  readonly PLAY = 'play_arrow';
  readonly PAUSE = 'pause';
  readonly REPLAY = 'refresh';
  readonly MIN_TOTAL_DURATION = 1000; // Minimum total adjusted duration in ms
  readonly MIN_SEGMENT_DURATION = 0; // Minimum per-segment duration in ms
  private SIMPLIFY_TOLERANCE = 0.0001; // Adjust based on testing

  private completedTrackIds: Set<number> = new Set();
  private totalDuration = 0;
  private windowSize = 0.15; // 15% of total duration
  private readonly REDUCED_WINDOW_SIZE = 0.1; // Reduce to 10% if needed
  private readonly TRACK_DENSITY_THRESHOLD = 15;
  private loadedWindows: Set<number> = new Set(); // Track which 15% windows are loaded
  private currentWindowEnd = 0;

  private playSub: number | null = null;
  private pausedState: {
    min: number;
    max: number;
    tracksState: { id: number; hasStarted: boolean; position: any }[];
  } | null = null;
  startBtn: string = this.PLAY;

  constructor() {
    const storedConfig = localStorage.getItem('playbackConfig');
    const parsedConfig = storedConfig ? JSON.parse(storedConfig) : {};
    this.defaultConfig = { ...this.defaultConfig, ...parsedConfig };

    this.defaultConfig.speedIndex = Math.max(
      0,
      Math.min(this.defaultConfig.speedIndex, this.TIME_SETTINGS.length - 1),
    );

    // Set up debounced subscription for slider changes
    this.sliderChangeSubscription = this.sliderChangeSubject
      .pipe(debounceTime(50))
      .subscribe((changeContext: { min: number; max: number }) =>
        this.handleSliderChange(changeContext),
      );
  }

  onInputsChanged() {
    this.resetMap();
    if (this.mapService.map) {
      this.resetPolymotionPaneIndex();
    }

    if (this.mapService.map) {
      this.motionLayerGroup = L.layerGroup().addTo(this.mapService.map);
    }
    if (this.mapPositions?.length) {
      this.initAnimationFilter(this.mapPositions);
    }
  }

  updateAutoFocus(autoFocusEnabled: boolean) {
    if (autoFocusEnabled !== this.defaultConfig.autoFocus) {
      this.defaultConfig.autoFocus = autoFocusEnabled;
      this.saveConfig();
    }
  }

  resetMap() {
    this.resetMotionPlaybackLayer();
    this.removeHighlightLayerGroup();
    this.resetValues();
  }

  initAnimationFilter(mapPositions: webDtos.MapPositionsList[]) {
    this.isProcessing = true;
    let minTime: number | null = null;
    let maxTime: number | null = null;
    this.motionTracks = [];
    this.fullTrackPositions = [];
    this.startBtn = this.PLAY;
    const currentTime = Date.now();

    mapPositions.forEach((mapPositionsList) => {
      const formattedPositions = this.calcService.formattedPositions(mapPositionsList);
      let simplifiedPoints = formattedPositions;

      if (formattedPositions.length > 50) {
        if (formattedPositions.length > 15000) {
          this.SIMPLIFY_TOLERANCE = 0.006;
        } else if (formattedPositions.length > 10000) {
          this.SIMPLIFY_TOLERANCE = 0.004;
        } else if (formattedPositions.length > 5000) {
          this.SIMPLIFY_TOLERANCE = 0.003;
        } else if (formattedPositions.length > 1000) {
          this.SIMPLIFY_TOLERANCE = 0.002;
        } else {
          this.SIMPLIFY_TOLERANCE = 0.0001;
        }
        simplifiedPoints = this.calcService.simplifyPositions(
          formattedPositions,
          this.SIMPLIFY_TOLERANCE,
        );
      }

      if (simplifiedPoints.length < 2) return;

      // Extract timestamps (assumes data is sorted)
      const earliestTime = new Date(simplifiedPoints[0]?.timestamp).getTime();
      const latestTime = new Date(
        simplifiedPoints[simplifiedPoints.length - 1]?.timestamp,
      ).getTime();

      if (latestTime > currentTime) {
        return;
      }

      if (!minTime || earliestTime < minTime) minTime = earliestTime;
      if (!maxTime || latestTime > maxTime) maxTime = latestTime;

      // Store full positions independently
      this.fullTrackPositions.push({
        id: mapPositionsList.id,
        positions: [...simplifiedPoints],
        color: mapPositionsList.color,
      });
    });

    if (this.fullTrackPositions.length === 0) {
      this.isProcessing = false;
      return;
    }

    // Sort fullTrackPositions once by startTime
    this.fullTrackPositions.sort((a, b) => a.positions[0].timestamp - b.positions[0].timestamp);

    // **Set Slider Range and calculate stepsize**
    if (minTime && maxTime) {
      this.initializeTimeRange({ minTime, maxTime });
    }

    // Load the first 15% window
    this.loadInitialWindow();

    this.initializeSliderOptions();

    // Fit map to bounds of all track positions
    if (minTime && maxTime) {
      this.highlightTrackRange(this.defaultConfig.autoFocus, false, minTime, maxTime);
    }
    this.isProcessing = false;
  }

  private loadInitialWindow(): void {
    const initialWindowEnd = this.floor + this.totalDuration * this.windowSize;
    let trackCount = 0;

    // First pass: Calculate track density
    this.fullTrackPositions.forEach((track) => {
      if (track.positions[0].timestamp <= initialWindowEnd) {
        trackCount++;
      }
    });

    this.windowSize =
      trackCount > this.TRACK_DENSITY_THRESHOLD ? this.REDUCED_WINDOW_SIZE : this.windowSize;
    this.currentWindowEnd = this.floor + this.totalDuration * this.windowSize;
    this.loadedWindows.clear();
    this.loadedWindows.add(0);

    // Second pass: Process tracks without adding to map
    const newMotionTracks: MotionTrack[] = [];
    this.fullTrackPositions.forEach((track) => {
      const startTime = track.positions[0].timestamp;
      if (startTime <= this.currentWindowEnd) {
        const mapPositionList = this.mapPositions?.find((c) => c.id === track.id);
        if (!mapPositionList) return;

        const motionTrack = this.createMotionTrack(
          track.positions,
          mapPositionList,
          this.defaultConfig.fadeOutOption,
        );

        newMotionTracks.push({
          motionTrack,
          positions: track.positions,
          startTime: startTime,
          endTime: track.positions[track.positions.length - 1].timestamp,
          hasStarted: false,
          id: track.id,
          color: track.color,
        });
      }
    });

    // Sort and batch-add to motionLayerGroup
    newMotionTracks.sort((a, b) => a.startTime - b.startTime);
    this.motionTracks = newMotionTracks;
    if (this.motionLayerGroup) {
      this.motionLayerGroup.clearLayers();
      this.motionTracks.forEach((track) => this.motionLayerGroup?.addLayer(track.motionTrack));
    }
  }

  private loadNextWindow(): void {
    const nextWindowStart = this.currentWindowEnd;
    const nextWindowEnd = nextWindowStart + this.totalDuration * this.windowSize;
    const windowIndex = Math.floor(
      (nextWindowStart - this.floor) / (this.totalDuration * this.windowSize),
    );

    if (this.loadedWindows.has(windowIndex) || nextWindowStart >= this.ceil) return;

    this.loadedWindows.add(windowIndex);
    this.currentWindowEnd = nextWindowEnd;

    const newMotionTracks: MotionTrack[] = [];
    this.fullTrackPositions.forEach((track) => {
      const startTime = track.positions[0].timestamp;
      if (
        startTime > nextWindowStart - this.totalDuration * this.windowSize &&
        startTime <= nextWindowEnd &&
        !this.motionTracks.some((t) => t.id === track.id)
      ) {
        const mapPositionList = this.mapPositions?.find((c) => c.id === track.id);
        if (!mapPositionList) return;

        const motionTrack = this.createMotionTrack(
          track.positions,
          mapPositionList,
          this.defaultConfig.fadeOutOption,
        );

        newMotionTracks.push({
          motionTrack,
          positions: track.positions,
          startTime: startTime,
          endTime: track.positions[track.positions.length - 1].timestamp,
          hasStarted: false,
          id: track.id,
          color: track.color,
        });
      }
    });

    // Add new tracks to existing motionTracks and update layer group
    this.motionTracks.push(...newMotionTracks);
    this.motionTracks.sort((a, b) => a.startTime - b.startTime);
    this.motionTracks.forEach((track) => this.motionLayerGroup?.addLayer(track.motionTrack));
  }

  fitBoundsForAllTracks() {
    if (this.fullTrackPositions.length > 0 && this.defaultConfig.autoFocus && this.mapService.map) {
      const bounds = this.calcService.calculateBounds(this.fullTrackPositions);
      if (bounds.isValid()) {
        setTimeout(() => {
          this.mapService.map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 18,
          });
        }, 100);
      }
    }
  }

  private initializeTimeRange(timeRange: TimeRange): void {
    this.floor = timeRange.minTime;
    this.ceil = timeRange.maxTime;
    this.minValue = timeRange.minTime;
    this.maxValue = timeRange.minTime + this.selectedTimeRange;

    this.totalDuration = this.ceil - this.floor;
    this.currentWindowEnd = this.floor + this.totalDuration * this.windowSize;
  }

  private initializeSliderOptions(): void {
    const stepSize = this.calcService.calculateStepSize(
      this.ceil,
      this.floor,
      this.TIME_SETTINGS[this.defaultConfig.speedIndex].value,
    );
    this.options = {
      floor: this.floor,
      ceil: this.ceil,
      step: stepSize,
      draggableRange: true,
      hideLimitLabels: true,
      disabled: false,
      animate: false,
      translate: (value: number): string => this.formatSliderValue(value),
    };

    // Precompute cache for key timestamps
    const step = stepSize > 0 ? stepSize : 1000;
    for (let t = this.floor; t <= this.ceil; t += step) {
      this.formatSliderValue(t);
    }
  }

  onSliderChange(changeContext: ChangeContext): void {
    this.sliderChangeSubject.next({ min: changeContext.value, max: changeContext.highValue });
  }

  handleSliderChange(changeContext: { min: number; max: number }): void {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.pausePlaying();

    this.clearPlaySub();
    this.resetMotionPlaybackLayer();
    this.removeHighlightLayerGroup();

    this.startBtn = this.PLAY;
    this.isSliderChanged = true;
    this.minValue = changeContext.min;
    this.maxValue = this.cachedMaxValue = changeContext.max;
    this.pausedState = null;

    this.highlightTrackRange(this.defaultConfig.autoFocus, true, this.minValue, this.maxValue);
    this.isProcessing = false;
  }

  private prepareTracksFromTimestamp(
    timestamp: number,
    pausedPositions?: Map<number, { latlng: L.LatLng; timestamp: number }>,
  ): void {
    // to make sure all tracks will be played again simultaneously
    const lowestPausedTs =
      this.pausedState && this.pausedState.tracksState.length > 0
        ? Math.min(...this.pausedState.tracksState.map((s) => s.position.timestamp))
        : timestamp;

    // Reset state
    this.motionTracks = [];
    this.completedTrackIds.clear();
    this.activeSegmentsQueue = [];
    this.activeSegments.clear();

    // Mark any fully completed tracks (all positions before the given timestamp)
    this.fullTrackPositions.forEach((track) => {
      const positions = track.positions;
      if (positions.length > 0) {
        const lastTimestamp = positions[positions.length - 1].timestamp;
        if (lastTimestamp < timestamp) {
          this.completedTrackIds.add(track.id);
        }
      }
    });

    const newMotionTracks: MotionTrack[] = [];
    this.fullTrackPositions.forEach((track) => {
      // Skip processing if this track is already completed
      if (this.completedTrackIds.has(track.id)) {
        return;
      }

      let filteredPositions: Position[] = [];

      // Check if there's a paused position for this track
      if (pausedPositions && pausedPositions.has(track.id)) {
        const pausedPosition = pausedPositions.get(track.id);
        if (!pausedPosition) {
          console.log('pausedPosition state is undefined skip the track', track.id);
          return;
        }
        filteredPositions.push({
          ...pausedPosition,
          timestamp: lowestPausedTs,
        });

        // Find positions after the paused timestamp
        const nextIndex = track.positions.findIndex(
          (pos) => pos.timestamp > pausedPosition.timestamp,
        );
        if (nextIndex !== -1) {
          filteredPositions.push(...track.positions.slice(nextIndex));
        } else if (track.positions.length > 0) {
          filteredPositions.push(track.positions[track.positions.length - 1]);
        }
      } else {
        // Default logic: find start index based on timestamp
        const firstIndex = track.positions.findIndex((pos) => pos.timestamp >= timestamp);
        if (firstIndex === -1) {
          // No position has timestamp >= timestamp, so nothing to animate
          return;
        }
        const startIndex = firstIndex === 0 ? 0 : firstIndex - 1;
        filteredPositions = track.positions.slice(startIndex);
      }

      if (filteredPositions.length < 2) return;

      const mapPositionList = this.mapPositions?.find((c) => c.id === track.id);
      if (!mapPositionList) return;

      const motionTrack = this.createMotionTrack(
        filteredPositions,
        mapPositionList,
        this.defaultConfig.fadeOutOption,
      );

      newMotionTracks.push({
        motionTrack,
        positions: filteredPositions,
        startTime: filteredPositions[0].timestamp,
        endTime: filteredPositions[filteredPositions.length - 1].timestamp,
        hasStarted: false,
        id: track.id,
        color: track.color,
      });
    });

    // Order by start time and render
    this.motionTracks = newMotionTracks.sort((a, b) => a.startTime - b.startTime);
    if (this.motionLayerGroup) {
      this.motionLayerGroup.clearLayers();
      this.motionTracks.forEach((track) => this.motionLayerGroup?.addLayer(track.motionTrack));
    }

    this.maxValue = timestamp;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  togglePlayPause(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.trackPlayedFirstTime = false;
    if (this.highlightMarkersGroup) {
      this.highlightMarkersGroup.clearLayers();
    }

    if (this.isSliderChanged) {
      if (this.ceil === this.maxValue) {
        this.donePlaying();
        this.isProcessing = false;
        this.isSliderChanged = false;
      } else {
        if (this.defaultConfig.fadeOutOption !== 1) {
          if (this.defaultConfig.fadeOutOption == 0) {
            this.minValue = this.floor;
          } else {
            this.minValue = this.maxValue - this.defaultConfig.fadeOutOption;
          }
          this.highlightTrackRange(
            this.defaultConfig.autoFocus,
            false,
            this.minValue,
            this.maxValue,
          );
        }
        this.updateSliderStepSize();
        this.prepareTracksFromTimestamp(this.maxValue);
        this.startAnimationFrame();
        this.startBtn = this.PAUSE;
        this.isProcessing = false;
        this.isSliderChanged = false;
      }
      return;
    }

    if (this.startBtn === this.PLAY) {
      if (!this.isSliderChanged) {
        if (this.highlightLayerGroup) {
          this.highlightLayerGroup.clearLayers();
        }
      }
      this.startPlaying();
      this.startBtn = this.PAUSE;
    } else if (this.startBtn === this.PAUSE) {
      this.clearPlaySub();
      this.pausedState = null;
      this.pausePlaying();
      this.startBtn = this.PLAY;
    } else if (this.startBtn === this.REPLAY) {
      this.removeHighlightLayerGroup();
      this.clearPlaySub();
      this.resetMotionPlaybackLayer();
      this.minValue = this.maxValue = this.floor;
      this.prepareTracksFromTimestamp(this.minValue);
      this.startAnimationFrame();
      this.startBtn = this.PAUSE;
    }

    this.isProcessing = false;
  }

  private startPlaying(): void {
    if (!this.motionTracks || this.motionTracks.length === 0) return;

    if (this.pausedState) {
      this.resumeFromPause();
      return;
    }

    this.startAnimationFrame();
  }

  private attachMotionEndEventListener(track: MotionTrack) {
    track.motionTrack.on('motion-ended', () => {
      this.activeSegments.delete(track.id);
      this.completedTrackIds.add(track.id);

      if (this.maxValue < track.endTime) {
        this.maxValue = track.endTime;
        this.updateMinValue();
      }

      if (this.completedTrackIds.size === this.fullTrackPositions.length) {
        this.maxValue = this.ceil;
        this.updateMinValue();
        this.donePlaying();
      }
    });
  }

  private startAnimationFrame(): void {
    if (this.playSub) return;

    let lastTimestamp: number | null = null;
    let frameCount = 0;
    let isZoomAdjusted = false;
    const recentTargets: L.LatLng[] = [];
    const maxRecentTargets = 10;

    const intervalMs = 50;
    const ticksPerSecond = 1000 / intervalMs;

    const animate = (currentTimestamp: number) => {
      if (!lastTimestamp) lastTimestamp = currentTimestamp;
      const deltaMs = currentTimestamp - lastTimestamp;
      lastTimestamp = currentTimestamp;

      const speedValue = this.TIME_SETTINGS[this.defaultConfig.speedIndex].value;
      const totalDuration = this.ceil - this.floor;
      const realSecondsToTraverse = totalDuration / (speedValue * 1000);
      const totalTicks = realSecondsToTraverse * ticksPerSecond;
      const baseIncrement = (totalDuration / totalTicks) * (deltaMs / (1000 / ticksPerSecond));
      this.updateMotionTracks(baseIncrement);

      if (this.maxValue >= this.currentWindowEnd - this.totalDuration * 0.05) {
        this.loadNextWindow();
      }

      const realTimePerFrame = (totalDuration / speedValue / totalTicks) * 1000;
      const minPanIntervalMs = 200;
      const maxPanIntervalMs = 5000;
      const speedFactor = speedValue / 43200;
      const adjustedSpeedFactor = speedFactor <= 1 ? 0 : Math.pow(speedFactor - 1, 2);
      const desiredPanIntervalMs =
        minPanIntervalMs +
        (maxPanIntervalMs - minPanIntervalMs) * Math.min(adjustedSpeedFactor / 10, 1);
      const panIntervalFrames = Math.max(Math.round(desiredPanIntervalMs / realTimePerFrame), 1);

      if (
        this.activeSegments.size > 0 &&
        this.defaultConfig.autoFocus &&
        frameCount++ % panIntervalFrames === 0 &&
        this.mapService.map
      ) {
        const rawTarget =
          this.activeSegments.size === 1
            ? Array.from(this.activeSegments.values())[0][0]
            : this.calcService.calculateCentroid(this.activeSegments);

        recentTargets.push(rawTarget);
        if (recentTargets.length > maxRecentTargets) recentTargets.shift();
        const smoothedTarget = this.calcService.smoothTargetLocation(
          recentTargets,
          this.lastPanLocation,
          this.mapService.map.getCenter(),
        );

        this.smoothPanTo(smoothedTarget);
        if (!isZoomAdjusted) {
          this.fitBoundsForAllTracks();
          isZoomAdjusted = true;
        }
      }

      if (this.maxValue < this.ceil || this.activeSegments.size > 0) {
        this.playSub = requestAnimationFrame(animate);
      } else {
        this.maxValue = this.ceil;
        this.donePlaying();
      }
      this.cdr.detectChanges();
    };

    this.playSub = requestAnimationFrame(animate);
  }

  private updateMotionTracks(baseIncrement: number): void {
    const updates: (() => void)[] = [];
    this.motionTracks.forEach((track) => {
      if (!track.hasStarted && this.maxValue >= track.startTime) {
        updates.push(() => {
          track.motionTrack.motionStart();
          track.hasStarted = true;
          this.attachMotionEndEventListener(track);
        });
      }

      const activeSegment = track.motionTrack._activeLayer;
      if (
        activeSegment &&
        !this.activeSegmentsQueue.some((s) => s.trackId === track.id && s.segment === activeSegment)
      ) {
        this.activeSegmentsQueue.push({
          trackId: track.id,
          segment: activeSegment,
          endTimestamp: activeSegment.endTimestamp,
        });
        const segmentLatLngs = activeSegment._latlngs;
        if (segmentLatLngs && segmentLatLngs.length) {
          this.activeSegments.set(track.id, segmentLatLngs);
        }
      }
    });

    updates.forEach((update) => update());

    this.activeSegmentsQueue = this.activeSegmentsQueue.filter((entry) => {
      const layer = this.motionTracks.find((t) => t.id === entry.trackId)?.motionTrack._activeLayer;
      return layer && layer === entry.segment;
    });
    this.activeSegmentsQueue.sort((a, b) => a.endTimestamp - b.endTimestamp);

    if (this.activeSegmentsQueue.length > 0) {
      const earliestActiveSegment = this.activeSegmentsQueue[0];
      if (this.maxValue < earliestActiveSegment.endTimestamp && this.maxValue < this.ceil) {
        const timeToNext = earliestActiveSegment.endTimestamp - this.maxValue;
        const increment = Math.min(timeToNext, baseIncrement);
        this.maxValue += increment;
        if (this.maxValue >= earliestActiveSegment.endTimestamp) {
          this.maxValue = earliestActiveSegment.endTimestamp;
        }
        this.updateMinValue();
      }
    } else {
      this.maxValue += baseIncrement;
      if (this.maxValue > this.ceil) this.maxValue = this.ceil;
      this.updateMinValue();
    }
  }

  private smoothPanTo(targetLocation: L.LatLng): void {
    const map = this.mapService.map;
    if (!map) return;
    const currentCenter = map.getCenter();
    const distance = this.calcService.getDistanceInPixels(currentCenter, targetLocation, map);
    if (distance < 80) return;

    this.lastPanLocation = targetLocation;

    const speedValue = this.TIME_SETTINGS[this.defaultConfig.speedIndex].value;
    const speedFactor = speedValue / 43200;
    const baseDuration = 1.0;
    const maxDuration = 5.0;
    const panDuration =
      baseDuration + (maxDuration - baseDuration) * Math.min((speedFactor - 1) / 10, 1);

    map.panTo(targetLocation, { animate: true, duration: panDuration });
  }

  private updateMinValue() {
    if (!this.options) return;
    const stepSize = this.options.step ?? 1;
    const fadeOutDuration = this.defaultConfig.fadeOutOption;
    const floor = this.options.floor ?? this.floor;
    const ceiling = this.options.ceil ?? this.ceil;

    if (
      this.maxValue - this.minValue > this.defaultConfig.fadeOutOption &&
      this.defaultConfig.fadeOutOption !== 1
    ) {
      if (this.defaultConfig.fadeOutOption == 0) {
        this.minValue = Math.max(Math.min(this.floor, ceiling), floor);
      } else {
        if (this.maxValue - this.minValue >= fadeOutDuration) {
          const targetMinValue = this.maxValue - fadeOutDuration;
          this.minValue = Math.round(targetMinValue / stepSize) * stepSize;
          this.minValue = Math.max(Math.min(this.minValue, ceiling), floor);
        }
        this.setOutsideRangeTracksToTransparent();
        this.removeOutsideRangeHighlights();
      }
    }
    this.minValue = Math.max(Math.min(this.minValue, ceiling), floor);
  }

  private removeOutsideRangeHighlights() {
    if (this.highlightLayerGroup) {
      this.highlightLayerGroup.eachLayer((layer: any) => {
        if (layer.endTimestamp < this.minValue) {
          this.highlightLayerGroup?.removeLayer(layer);
        }
      });

      if (this.highlightLayerGroup.getLayers().length === 0) {
        this.highlightLayerGroup.clearLayers();
      }
    }
  }

  private setOutsideRangeTracksToTransparent() {
    this.motionTracks.forEach((track) => {
      if (!track.motionTrack) return;
      const segments = Object.values(track.motionTrack._layers) as any[];
      segments.forEach((segment) => {
        if (segment.setStyle && segment.endTimestamp < this.minValue) {
          segment.setStyle({
            color: 'transparent',
            weight: 0,
            opacity: 0,
          });
        }
      });
    });
  }

  private clearPlaySub(): void {
    if (this.playSub !== null && this.playSub !== undefined) {
      cancelAnimationFrame(this.playSub);
      this.playSub = null;
    }
  }

  private pausePlaying(): void {
    if (this.motionTracks.length === 0) return;

    this.pausedState = {
      min: this.minValue,
      max: this.maxValue,
      tracksState: this.motionTracks
        .filter((track) => track.hasStarted && track.motionTrack && track.motionTrack._activeLayer)
        .map((track) => {
          track.motionTrack.motionPause();
          const activeLayer = track.motionTrack._activeLayer;

          return {
            id: track.id,
            hasStarted: track.hasStarted,
            position: {
              latlng: activeLayer.__marker._latlng,
              timestamp: activeLayer.startTimestamp + activeLayer.__ellapsedTime,
            },
          };
        }),
    };
  }

  private resumeFromPause(): void {
    if (!this.pausedState) return;
    this.isProcessing = true;

    const pausedPositions = new Map<number, { latlng: L.LatLng; timestamp: number }>();
    this.pausedState.tracksState.forEach((state) => {
      if (state.hasStarted && state.position && !this.completedTrackIds.has(state.id)) {
        pausedPositions.set(state.id, state.position);
      }
    });

    this.resetMotionPlaybackLayer();
    if (this.pausedState) {
      if (this.defaultConfig.fadeOutOption != 1 && this.defaultConfig.fadeOutOption != 0) {
        this.minValue = this.pausedState.max - this.defaultConfig.fadeOutOption;
      }
      this.maxValue = this.pausedState.max;
    } else {
      console.log(' unknown error');
    }

    this.prepareTracksFromTimestamp(this.pausedState.max, pausedPositions);
    this.highlightTrackRange(false, false, this.minValue, this.maxValue, pausedPositions);
    this.startAnimationFrame();
    this.startBtn = this.PAUSE;
    this.isProcessing = false;
  }

  fadeOutRangeChange(value: number) {
    this.defaultConfig.fadeOutOption = value;
    this.saveConfig();

    this.minValue = this.maxValue = this.floor;

    if (this.highlightMarkersGroup) {
      this.highlightMarkersGroup.clearLayers();
    }
    this.removeHighlightLayerGroup();
    this.clearPlaySub();
    this.resetMotionPlaybackLayer();
    if (this.mapPositions) {
      this.initAnimationFilter(this.mapPositions);
    }
    this.pausedState = null;
  }

  fastForward(): void {
    this.defaultConfig.speedIndex = Math.min(
      this.defaultConfig.speedIndex + 1,
      this.TIME_SETTINGS.length - 1,
    );
    const newSpeedValue = this.TIME_SETTINGS[this.defaultConfig.speedIndex].value;
    console.log(
      `Fast forward: speed set to ${this.TIME_SETTINGS[this.defaultConfig.speedIndex].label}`,
    );

    this.motionTracks.forEach((track) => {
      if (!track.motionTrack) return;

      const segments = Object.values(track.motionTrack._layers) as any[];
      let totalOriginalDuration = 0;

      segments.forEach((segment) => {
        if (segment.motionOptions?.duration) {
          const originalDuration =
            segment._originalDuration || segment.motionOptions.duration * newSpeedValue;
          totalOriginalDuration += originalDuration;
        }
      });

      const baseTotalAdjustedDuration = totalOriginalDuration / newSpeedValue;
      const scalingFactor =
        baseTotalAdjustedDuration < this.MIN_TOTAL_DURATION
          ? this.MIN_TOTAL_DURATION / baseTotalAdjustedDuration
          : 1;

      segments.forEach((segment) => {
        if (segment.motionOptions?.duration) {
          const originalDuration =
            segment._originalDuration || segment.motionOptions.duration * newSpeedValue;
          let adjustedDuration = (originalDuration / newSpeedValue) * scalingFactor;
          adjustedDuration = Math.max(Math.round(adjustedDuration), this.MIN_SEGMENT_DURATION);
          segment.motionOptions.duration = adjustedDuration;
        }
      });
    });

    this.updateSliderStepSize();
    this.saveConfig();
  }

  slowForward(): void {
    this.defaultConfig.speedIndex = Math.max(this.defaultConfig.speedIndex - 1, 0);
    const newSpeedValue = this.TIME_SETTINGS[this.defaultConfig.speedIndex].value;
    console.log(
      `Slow forward: speed set to ${this.TIME_SETTINGS[this.defaultConfig.speedIndex].label}`,
    );

    this.motionTracks.forEach((track) => {
      if (!track.motionTrack) return;
      Object.values(track.motionTrack._layers).forEach((segment: any) => {
        if (segment.motionOptions?.duration) {
          const originalDuration =
            segment._originalDuration || segment.motionOptions.duration * newSpeedValue;
          segment.motionOptions.duration = originalDuration / newSpeedValue;
        }
      });
    });

    this.updateSliderStepSize();
    this.saveConfig();
  }

  private updateSliderStepSize(): void {
    const newStepSize = this.calcService.calculateStepSize(
      this.ceil,
      this.floor,
      this.TIME_SETTINGS[this.defaultConfig.speedIndex].value,
    );
    this.options = {
      ...this.options,
      floor: this.floor,
      ceil: this.ceil,
      step: newStepSize,
    };
    this.sliderStepSize = newStepSize;
  }

  private resetMotionPlaybackLayer() {
    if (this.motionLayerGroup) {
      this.motionLayerGroup.eachLayer((layer) => {
        if (layer.off) {
          layer.off('motion-ended');
        }
      });
      this.motionLayerGroup.clearLayers();
    }
    this.activeSegmentsQueue = [];
  }

  private createMotionTrack(
    positions: Position[],
    collar: webDtos.MapPositionsList,
    fadeOutOption: number,
  ) {
    const motionSegments = [];
    const speedValue = this.TIME_SETTINGS[this.defaultConfig.speedIndex].value;
    const durations: number[] = [];
    let totalOriginalDuration = 0;
    for (let i = 0; i < positions.length - 1; i++) {
      const duration = positions[i + 1].timestamp - positions[i].timestamp;
      durations.push(duration);
      totalOriginalDuration += duration;
    }

    const baseTotalAdjustedDuration = totalOriginalDuration / speedValue;
    const scalingFactor =
      baseTotalAdjustedDuration < this.MIN_TOTAL_DURATION
        ? this.MIN_TOTAL_DURATION / baseTotalAdjustedDuration
        : 1;

    if (!(L as any).motion) {
      console.error('Leaflet.motion plugin is not loaded. Cannot create motion track.');
      throw new Error('Leaflet.motion plugin is not loaded.');
    }

    let icon;
    const specie = this.speciesMap?.get(collar.id);
    if (specie?.icon) {
      icon = this.mapMarkerService.createColoredSvgIcon(specie.icon, collar.color);
    } else {
      icon = L.icon({ iconUrl: baseHref + 'assets/img/ship.png' });
    }

    for (let i = 0; i < positions.length - 1; i++) {
      const start = positions[i];
      const end = positions[i + 1];
      const adjustedDuration = Math.max(
        Math.round((durations[i] / speedValue) * scalingFactor),
        this.MIN_SEGMENT_DURATION,
      );

      const segment = (L as any).motion.polyline(
        [start.latlng, end.latlng],
        {
          color: fadeOutOption == 1 ? 'transparent' : collar.color,
          weight: fadeOutOption == 1 ? 0 : this.DEFAULT_LINE_WEIGHT,
          opacity: fadeOutOption == 1 ? 0 : this.MOTION_OPACITY,
          interactive: false,
        },
        {
          duration: adjustedDuration,
          easing: (L as any).Motion.Ease.linear,
          auto: false,
        },
        {
          removeOnEnd: true,
          showMarker: false,
          icon: icon,
        },
      );

      segment.startTimestamp = start.timestamp;
      segment.endTimestamp = end.timestamp;
      segment.id = collar.id;
      segment._originalDuration = durations[i];
      motionSegments.push(segment);
    }

    return (L as any).motion.seq(motionSegments);
  }

  toggleAutoFocus(): void {
    this.defaultConfig.autoFocus = !this.defaultConfig.autoFocus;
    this.saveConfig();
  }

  private donePlaying(): void {
    this.startBtn = this.REPLAY;
    this.pausedState = null;
    this.completedTrackIds.clear();
    this.clearPlaySub();

    if (!this.highlightMarkersGroup && this.mapService.map) {
      this.highlightMarkersGroup = L.layerGroup().addTo(this.mapService.map);
    }
    this.highlightMarkersGroup?.clearLayers();

    this.fullTrackPositions.forEach((track) => {
      const latestPosition = track.positions[track.positions.length - 1];
      if (latestPosition && latestPosition.latlng) {
        let icon;
        const specie = this.speciesMap?.get(track.id);
        if (specie?.icon) {
          icon = this.mapMarkerService.createColoredSvgIcon(specie.icon, track.color);
        } else {
          icon = L.icon({ iconUrl: baseHref + 'assets/img/ship.png' });
        }

        const marker = L.marker(latestPosition.latlng, {
          icon: icon,
        });
        this.highlightMarkersGroup?.addLayer(marker);
      }
    });
  }

  private removeHighlightLayerGroup() {
    if (this.highlightLayerGroup) {
      this.highlightLayerGroup.remove();
      this.highlightLayerGroup = null;
    }
    if (this.highlightMarkersGroup) {
      this.highlightMarkersGroup.clearLayers();
      this.highlightMarkersGroup = null;
    }
  }

  private resetValues() {
    this.pausedState = null;
    this.minValue = this.floor;
    this.maxValue = this.floor + 0;
    this.cachedMaxValue = null;
    this.startBtn = this.PLAY;
    this.options = undefined;

    this.clearPlaySub();
  }

  private formatSliderValue(timestamp: number): string {
    if (!this.dateCache) this.dateCache = new Map();
    if (this.dateCache.size > 10000) this.dateCache.clear();
    if (this.dateCache.has(timestamp)) return this.dateCache.get(timestamp) ?? '';

    const roundedTimestamp = Math.round(timestamp / 1000) * 1000;
    const formatted = formatDate(new Date(roundedTimestamp), 'yyyy-MM-dd HH:mm', 'en-US');
    this.dateCache.set(timestamp, formatted);
    this.dateCache.set(roundedTimestamp, formatted);
    return formatted;
  }

  getIcon(): string {
    return this.startBtn;
  }

  private resetPolymotionPaneIndex() {
    if (this.mapService.map?.getPanes()) {
      const panes = this.mapService.map.getPanes();
      const polymotionPane = panes['polymotionPane'];
      if (polymotionPane) {
        polymotionPane.style.zIndex = '599';
      }
    }
  }

  private reducePolymotionPaneIndex() {
    if (this.mapService.map?.getPanes()) {
      const panes = this.mapService.map.getPanes();
      const polymotionPane = panes['polymotionPane'];
      if (polymotionPane) {
        polymotionPane.style.zIndex = '300';
      }
    }
  }

  private highlightTrackRange(
    shouldSetFocus: boolean,
    showMarker = true,
    minValue: number,
    maxValue: number,
    pausedPositions?: Map<number, { latlng: L.LatLng; timestamp: number }>,
  ): void {
    if (this.highlightLayerGroup) {
      this.highlightLayerGroup.clearLayers();
    }

    if (this.highlightMarkersGroup) {
      this.highlightMarkersGroup.clearLayers();
    }

    if (this.mapService.map) {
      this.highlightMarkersGroup = L.layerGroup().addTo(this.mapService.map);
      this.highlightLayerGroup = L.layerGroup().addTo(this.mapService.map);
    }
    const rangeLatLngs: L.LatLng[] = [];

    this.fullTrackPositions.forEach((track) => {
      const rangePositions = track.positions.filter(
        (p) => p.timestamp >= minValue && p.timestamp <= maxValue,
      );

      // Add paused position if it exists and is within range
      if (pausedPositions && pausedPositions.has(track.id)) {
        const paused = pausedPositions.get(track.id);
        if (paused) {
          rangePositions.push({
            latlng: paused.latlng,
            timestamp: paused.timestamp,
          });
        }
      }

      if (rangePositions.length === 0) return;

      rangePositions.sort((a, b) => a.timestamp - b.timestamp);
      const latlngs = rangePositions.map((p) => p.latlng);

      const polyline = L.polyline(latlngs, {
        color:
          this.defaultConfig.fadeOutOption !== 1 || this.trackPlayedFirstTime
            ? track.color || 'yellow'
            : 'transparent',
        weight:
          this.defaultConfig.fadeOutOption !== 1 || this.trackPlayedFirstTime
            ? this.DEFAULT_LINE_WEIGHT
            : 0,
        opacity:
          this.defaultConfig.fadeOutOption !== 1 || this.trackPlayedFirstTime
            ? this.MOTION_OPACITY
            : 0,
      });

      (L as any).startTimestamp = rangePositions[0].timestamp;
      (L as any).endTimestamp = rangePositions[rangePositions.length - 1].timestamp;
      this.highlightLayerGroup?.addLayer(polyline);

      const latestPosition = rangePositions[rangePositions.length - 1];
      if (latestPosition && latestPosition.latlng && showMarker) {
        let icon;
        const specie = this.speciesMap?.get(track.id);
        if (specie?.icon) {
          icon = this.mapMarkerService.createColoredSvgIcon(specie.icon, track.color);
        } else {
          icon = L.icon({ iconUrl: baseHref + 'assets/img/ship.png' });
        }

        const marker = L.marker(latestPosition.latlng, {
          icon: icon,
        });
        this.highlightMarkersGroup?.addLayer(marker);
      }

      latlngs.forEach((latlng) => rangeLatLngs.push(latlng));
    });

    if (rangeLatLngs.length > 0 && shouldSetFocus && this.mapService.map) {
      const rangeBounds = L.latLngBounds(rangeLatLngs);
      if (rangeBounds.isValid()) {
        this.mapService.map.fitBounds(rangeBounds, {
          padding: [150, 150],
          maxZoom: 18,
        });
      }
    }
  }

  ngOnDestroy() {
    this.onDestroy();
  }

  onDestroy() {
    this.clearPlaySub();
    this.resetMotionPlaybackLayer();
    this.removeHighlightLayerGroup();
    this.reducePolymotionPaneIndex();
    this.resetValues();

    if (this.sliderChangeSubscription) {
      this.sliderChangeSubscription.unsubscribe();
    }
    this.sliderChangeSubject.complete();
  }

  saveConfig() {
    localStorage.setItem('playbackConfig', JSON.stringify(this.defaultConfig));
  }
}
